import _ from 'lodash';
import uniqolor from 'uniqolor';
import { typedHttpClient } from '../utils/httpClient';
import {
  getOneOfVariants,
  pickOneOfVariant,
  resolveSchemaForForm,
} from './clientConfigSchemaResolve';

const isNaNNumber = (value: unknown): boolean => typeof value === 'number' && Number.isNaN(value);

/**
 * Merge a partial form submit into the persisted config (e.g. lazy-mounted accordion fields).
 * Arrays from the submission replace the previous value entirely (lodash default merge merges by index).
 */
const omitSpuriousNullSubmissionValues = (persisted: unknown, submission: unknown): unknown => {

  if (submission === null || isNaNNumber(submission)) {

    if (persisted === undefined || persisted === null || isNaNNumber(persisted)) {

      return undefined;

    }
    return null;

  }
  if (Array.isArray(submission)) {

    return submission;

  }
  if (!_.isPlainObject(submission)) {

    return submission;

  }

  const persistedObject: Record<string, unknown> = _.isPlainObject(persisted)
    ? persisted as Record<string, unknown>
    : {};
  const result: Record<string, unknown> = {};
  for (const [key, submittedValue] of Object.entries(submission as Record<string, unknown>)) {

    const sanitized = omitSpuriousNullSubmissionValues(persistedObject[key], submittedValue);
    if (sanitized !== undefined) {

      result[key] = sanitized;

    }

  }
  return result;

};

export const mergeConfigWithPartialFormSubmission = (
  persisted: Record<string, unknown> | undefined,
  submission: Record<string, unknown>,
) => {

  const sanitizedSubmission = omitSpuriousNullSubmissionValues(persisted, submission);
  const submissionRecord = _.isPlainObject(sanitizedSubmission) ? sanitizedSubmission : {};
  return _.mergeWith(
    _.cloneDeep(persisted ?? {}),
    submissionRecord as Record<string, unknown>,
    (_obj: unknown, srcVal: unknown) => (Array.isArray(srcVal) ? [...srcVal] : undefined),
  );

};

/** True when a value carries no config (null, empty containers, or nested shells with no leaves). */
export const isStructurallyEmptyClientConfigValue = (value: unknown): boolean => {

  if (value === null || value === undefined || isNaNNumber(value)) {

    return true;

  }
  if (Array.isArray(value)) {

    return value.length === 0 || value.every(isStructurallyEmptyClientConfigValue);

  }
  if (_.isPlainObject(value)) {

    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    return keys.length === 0 || keys.every((key) => isStructurallyEmptyClientConfigValue(record[key]));

  }
  return false;

};

/** Top-level default unset: null, undefined, or empty structure from stripped partial forms. */
export const isUnsetClientConfigDefaultValue = (value: unknown): boolean => (
  isStructurallyEmptyClientConfigValue(value)
);

export const hasClientConfigDefaultValueChanged = (original: unknown, updated: unknown): boolean => {

  if (isUnsetClientConfigDefaultValue(original) && isUnsetClientConfigDefaultValue(updated)) {

    return false;

  }
  return !_.isEqual(original, updated);

};

const isUnsetOptionalValue = isStructurallyEmptyClientConfigValue;

const isOptionalClientConfigProperty = (
  parentSchema: Record<string, any>,
  key: string,
  propSchema: Record<string, any> | undefined,
): boolean => {

  const resolvedPropSchema = propSchema
    ? resolveSchemaForForm(propSchema) ?? propSchema
    : undefined;
  if (resolvedPropSchema?.['x-optional'] === true) {

    return true;

  }
  if (parentSchema.required?.includes(key)) {

    return false;

  }
  if (resolvedPropSchema?.nullable) {

    return true;

  }
  return true;

};

const isObjectLikeClientConfigSchema = (propSchema: Record<string, any> | undefined): boolean => {

  const resolved = propSchema ? resolveSchemaForForm(propSchema) ?? propSchema : undefined;
  return resolved?.type === 'object' || Boolean(resolved?.properties);

};

const isArrayLikeClientConfigSchema = (propSchema: Record<string, any> | undefined): boolean => {

  const resolved = propSchema ? resolveSchemaForForm(propSchema) ?? propSchema : undefined;
  return resolved?.type === 'array';

};

const emptyRequiredClientConfigValue = (propSchema: Record<string, any> | undefined): unknown => {

  if (isObjectLikeClientConfigSchema(propSchema)) {

    return {};

  }
  if (isArrayLikeClientConfigSchema(propSchema)) {

    return [];

  }
  return undefined;

};

/**
 * Pick the schema describing `item`, keeping nested unions unresolved: resolveSchemaForForm
 * merges every oneOf variant it finds, so a variant-only required property (e.g. `options`
 * on a `select` form field) would be injected into unrelated variants further down.
 */
const resolveActiveItemSchema = (itemSchema: Record<string, any> | undefined, item: unknown) => {

  const variants = getOneOfVariants(itemSchema);
  if (!variants) {

    return itemSchema;

  }
  if (_.isPlainObject(item)) {

    const record = item as Record<string, unknown>;
    for (const discriminator of ['mergeKey', 'type']) {

      const variant = pickOneOfVariant(variants, discriminator, record[discriminator]);
      if (variant) {

        return variant;

      }

    }

  }
  // No discriminator match: merging the variants is the best available guess.
  return resolveSchemaForForm(itemSchema) ?? itemSchema;

};

/** Drop null/NaN leaves before PATCH when schema-aware strip cannot resolve nested properties. */
const omitNullLeavesDeep = (value: unknown): unknown => {

  if (value === null || value === undefined || isNaNNumber(value)) {

    return undefined;

  }
  if (Array.isArray(value)) {

    return value
      .map(omitNullLeavesDeep)
      .filter((item) => item !== undefined);

  }
  if (_.isPlainObject(value)) {

    const result: Record<string, unknown> = {};
    for (const [key, propValue] of Object.entries(value)) {

      if (propValue === null || isNaNNumber(propValue)) {

        continue;

      }
      const cleaned = omitNullLeavesDeep(propValue);
      if (cleaned !== undefined) {

        result[key] = cleaned;

      }

    }
    return result;

  }
  return value;

};

/** Drop null/undefined (and empty optional objects) so partial overrides omit unset fields. */
export const stripUnsetOptionalClientConfigValue = (value: unknown, schema: Record<string, any> | undefined): unknown => {

  if (value === null || value === undefined || isNaNNumber(value)) {

    return undefined;

  }

  if (Array.isArray(value)) {

    const itemSchema = schema?.items;
    if (!itemSchema) {

      return omitNullLeavesDeep(value);

    }
    const strippedItems = value
      .map((item) => stripUnsetOptionalClientConfigValue(item, resolveActiveItemSchema(itemSchema, item)))
      .filter((item) => !isStructurallyEmptyClientConfigValue(item));
    return strippedItems;

  }

  if (!_.isPlainObject(value)) {

    return value;

  }

  const resolvedSchema = resolveActiveItemSchema(schema, value);
  if (!resolvedSchema?.properties) {

    return omitNullLeavesDeep(value);

  }

  const result: Record<string, unknown> = {};
  for (const [key, propValue] of Object.entries(value as Record<string, unknown>)) {

    const propSchema = resolvedSchema.properties[key];
    if (!propSchema) {

      continue;

    }

    let stripped = stripUnsetOptionalClientConfigValue(propValue, propSchema);
    if (isUnsetOptionalValue(stripped)) {

      if (isOptionalClientConfigProperty(resolvedSchema, key, propSchema)) {

        continue;

      }
      const requiredEmptyValue = emptyRequiredClientConfigValue(propSchema);
      if (requiredEmptyValue === undefined) {

        if (propValue !== undefined && propValue !== null && !isNaNNumber(propValue)) {

          result[key] = propValue;

        }
        continue;

      }
      stripped = stripUnsetOptionalClientConfigValue(requiredEmptyValue, propSchema);

    }
    if (stripped !== undefined && stripped !== null && !isNaNNumber(stripped)) {

      result[key] = stripped;

    }

  }

  for (const key of resolvedSchema.required ?? []) {

    if (result[key] !== undefined) {

      continue;

    }
    const propSchema = resolvedSchema.properties[key];
    if (!propSchema) {

      continue;

    }
    const requiredEmptyValue = emptyRequiredClientConfigValue(propSchema);
    if (requiredEmptyValue === undefined) {

      continue;

    }
    result[key] = stripUnsetOptionalClientConfigValue(requiredEmptyValue, propSchema);

  }

  return result;

};

/** Normalize a defaults form value before PATCH: omit unset fields and never send non-nullable null. */
export const prepareClientConfigDefaultValueForUpdate = (
  value: unknown,
  schema: Record<string, any> | undefined,
): unknown => {

  const prepared = omitNullLeavesDeep(stripUnsetOptionalClientConfigValue(value, schema));
  if (isUnsetClientConfigDefaultValue(prepared)) {

    return undefined;

  }
  return prepared;

};

/** Top-level defaults save: use JSON null to clear (undefined is dropped from PATCH bodies). */
export const prepareTopLevelClientConfigDefaultForUpdate = (
  mergedValue: unknown,
  originalValue: unknown,
  schema: Record<string, any> | undefined,
): unknown => {

  const prepared = prepareClientConfigDefaultValueForUpdate(mergedValue, schema);
  if (prepared !== undefined) {

    return prepared;

  }
  if (!isUnsetClientConfigDefaultValue(originalValue) && isUnsetClientConfigDefaultValue(mergedValue)) {

    return null;

  }
  return undefined;

};

export type ClientConfigDefaultsPlatform = 'app' | 'kiosk';

export type ClientConfigOsFilter = {
  type: 'osFilter';
  osType: 'ios' | 'android';
  osVersionRange?: string;
};

export type ClientConfigOsFilterFormFields = {
  osFilterEnabled: boolean;
  osFilterOsType: 'ios' | 'android' | '';
  osFilterOsVersionRange: string;
};

export type ClientConfigKeyOverrideFormEntry = {
  osType: 'ios' | 'android';
  osVersionRange?: string;
  value: unknown;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
};

export type ClientConfigKeyRecord = {
  key: string;
  platform: 'app' | 'kiosk';
  version: number;
  defaultValue?: unknown;
  overrides?: Array<{
    value: unknown;
    filter: ClientConfigOsFilter;
    editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
  }>;
};

export const CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY = '_clientConfigKeyOverrides';

export function parseOsFilterFormFields(filter?: ClientConfigOsFilter | null): ClientConfigOsFilterFormFields {

  if (!filter || filter.type !== 'osFilter' || !filter.osType) {

    return {
      osFilterEnabled: false,
      osFilterOsType: '',
      osFilterOsVersionRange: '',
    };

  }
  return {
    osFilterEnabled: true,
    osFilterOsType: filter.osType,
    osFilterOsVersionRange: filter.osVersionRange || '',
  };

}

export function buildOsFilterPayload(
  enabled: boolean,
  osType: 'ios' | 'android' | '',
  osVersionRange?: string,
): ClientConfigOsFilter | undefined {

  if (!enabled || !osType) {

    return undefined;

  }
  const trimmedRange = osVersionRange?.trim();
  return {
    type: 'osFilter',
    osType,
    ...(trimmedRange ? { osVersionRange: trimmedRange } : {}),
  };

}

export function formatOsFilterChipLabel(
  filter: ClientConfigOsFilter | undefined,
  labels: { allDevices: string; ios: string; android: string },
): string {

  if (!filter?.osType) {

    return labels.allDevices;

  }
  const osLabel = filter.osType === 'ios' ? labels.ios : labels.android;
  if (filter.osVersionRange?.trim()) {

    return `${osLabel} ${filter.osVersionRange.trim()}`;

  }
  return osLabel;

}

export function normalizeKeyOverridesForUpsert(
  entries: ClientConfigKeyOverrideFormEntry[],
): ClientConfigKeyRecord['overrides'] {

  return entries
    .filter((entry) => entry?.osType)
    .map((entry) => ({
      value: entry.value,
      filter: {
        type: 'osFilter' as const,
        osType: entry.osType,
        ...(entry.osVersionRange?.trim() ? { osVersionRange: entry.osVersionRange.trim() } : {}),
      },
      ...(entry.editableBy ? { editableBy: entry.editableBy } : {}),
    }));

}

export function buildOverridesFormRecord(
  keys: ClientConfigKeyRecord[],
): Record<string, ClientConfigKeyOverrideFormEntry[]> {

  const record: Record<string, ClientConfigKeyOverrideFormEntry[]> = {};
  for (const keyRecord of keys) {

    if (!keyRecord.overrides?.length) {

      continue;

    }
    record[keyRecord.key] = keyRecord.overrides.map((override) => ({
      osType: override.filter.osType,
      osVersionRange: override.filter.osVersionRange || '',
      value: override.value,
      ...(override.editableBy ? { editableBy: override.editableBy } : {}),
    }));

  }
  return record;

}

export function areKeyOverridesEqual(
  left: ClientConfigKeyOverrideFormEntry[] | undefined,
  right: ClientConfigKeyOverrideFormEntry[] | undefined,
): boolean {

  return _.isEqual(normalizeKeyOverridesForUpsert(left || []), normalizeKeyOverridesForUpsert(right || []));

}

const CLIENT_CONFIG_KEYS_PAGE_SIZE = 200;

export async function fetchClientConfigKeysByPlatform(
  platform: ClientConfigDefaultsPlatform,
  version = 0,
): Promise<Record<string, ClientConfigKeyRecord>> {

  let totalItems = -1;
  const allKeys: ClientConfigKeyRecord[] = [];
  let offset = 0;
  let emptyRetriesRemaining = 2;

  while (totalItems < 0 || offset < totalItems) {

    const response = await typedHttpClient().clientConfig.clientConfigControllerPaginateKeys({
      $offset: offset,
      $max: CLIENT_CONFIG_KEYS_PAGE_SIZE,
    });
    const batch = response.data?.result || [];

    if (totalItems < 0) {

      const reportedTotal = response.data?.total;
      totalItems = typeof reportedTotal === 'number' && Number.isFinite(reportedTotal) && reportedTotal >= 0
        ? reportedTotal
        : batch.length;

    }

    if (totalItems === 0) {

      break;

    }

    if (batch.length === 0) {

      if (emptyRetriesRemaining <= 0) {

        break;

      }
      emptyRetriesRemaining--;
      continue;

    }

    emptyRetriesRemaining = 2;
    allKeys.push(...(batch as ClientConfigKeyRecord[]));
    offset += batch.length;

  }

  const keys = allKeys.filter((record) => record.platform === platform && record.version === version);
  return _.keyBy(keys, 'key') as Record<string, ClientConfigKeyRecord>;

}

export type ClientConfigDefaultDefinition = {
  key: string;
  schema?: Record<string, any>;
};

type ClientConfigKeyUpsertPromise = ReturnType<
  ReturnType<typeof typedHttpClient>['clientConfig']['clientConfigControllerUpsertKeyByBody']
>;

export const saveClientConfigDefaults = async (
  platform: ClientConfigDefaultsPlatform,
  version: number,
  definition: ClientConfigDefaultDefinition[],
  config: Record<string, unknown> | undefined,
  values: Record<string, unknown>,
  keysByPath?: Record<string, ClientConfigKeyRecord>,
): Promise<boolean> => {

  const {
    [CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY]: overridesByKey = {},
    ...configValues
  } = values;
  const mergedValues = mergeConfigWithPartialFormSubmission(config, configValues);
  const clientConfig = typedHttpClient().clientConfig;
  const updatePromises: ClientConfigKeyUpsertPromise[] = [];
  const initialOverridesByKey = keysByPath ? buildOverridesFormRecord(Object.values(keysByPath)) : {};

  for (const def of definition) {

    const originalValue = _.get(config, def.key);
    const mergedValue = _.get(mergedValues, def.key);
    const newValue = prepareTopLevelClientConfigDefaultForUpdate(mergedValue, originalValue, def.schema);
    // Compare prepared against prepared: newValue is normalized (null leaves dropped, properties
    // outside the schema dropped, required empties filled in), so comparing it against the raw
    // persisted value would report every normalization as a user edit.
    const preparedOriginalValue = prepareClientConfigDefaultValueForUpdate(originalValue, def.schema);
    const defaultChanged = hasClientConfigDefaultValueChanged(preparedOriginalValue, newValue) && newValue !== undefined;

    const submittedOverrides = (overridesByKey as Record<string, ClientConfigKeyOverrideFormEntry[]>)[def.key];
    const initialOverrides = initialOverridesByKey[def.key];
    const overridesChanged = typeof submittedOverrides !== 'undefined'
      && !areKeyOverridesEqual(submittedOverrides, initialOverrides);

    if (!defaultChanged && !overridesChanged) {

      continue;

    }

    const payload: {
      key: string;
      defaultValue?: unknown;
      overrides?: ClientConfigKeyRecord['overrides'];
    } = { key: def.key };

    if (defaultChanged) {

      payload.defaultValue = newValue;

    }
    if (overridesChanged) {

      payload.overrides = normalizeKeyOverridesForUpsert(submittedOverrides || []);

    }

    updatePromises.push(clientConfig.clientConfigControllerUpsertKeyByBody(
      platform,
      version,
      payload,
    ));

  }

  if (updatePromises.length === 0) {

    return false;

  }

  try {

    await Promise.all(updatePromises);
    return false;

  } catch {

    return true;

  }

};

export type ClientConfigGroupRef = {
  _id: string;
  _ref: 'ClientConfigGroup';
};

export type AppGroupDefinition = {
  type: 'group';
  group: ClientConfigGroupRef;
};

export type AppKeyDefinition = {
  type: 'key';
  key: string;
  value: any;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
};

export type AppDefinition = AppGroupDefinition | AppKeyDefinition;

export type AppConfigEntry = {
  definitions: AppDefinition[];
  filter?: ClientConfigOsFilter;
};

export type AppClientConfigContainer = {
  app?: AppConfigEntry[];
};

export type ClientConfigGroupLike = {
  _id: string;
  name: string;
  customer?: {
    _id: string;
    _ref?: string;
  } | null;
  clientConfig?: AppClientConfigContainer;
};

export function isClientConfigGroupGlobal(group?: Pick<ClientConfigGroupLike, 'customer'> | null): boolean {

  return !group?.customer?._id;

}

export function isClientConfigGroupCustomerSpecific(group?: Pick<ClientConfigGroupLike, 'customer'> | null): boolean {

  return Boolean(group?.customer?._id);

}

export function isClientConfigGroupVisibleForCustomer(
  group?: Pick<ClientConfigGroupLike, 'customer'> | null,
  currentCustomerId?: string | null,
): boolean {

  const groupCustomerId = group?.customer?._id;
  if (!groupCustomerId) {

    return true;

  }
  return Boolean(currentCustomerId && groupCustomerId === currentCustomerId);

}

export function getDisplayedSourceGroupIds(
  sourceGroupIds: string[],
  currentPathSources: string[],
  removedGroupIds: Set<string>,
  isLeaf: boolean,
): string[] {

  if (isLeaf) {

    return currentPathSources;

  }
  return sourceGroupIds.filter((groupId) => !removedGroupIds.has(groupId) || currentPathSources.includes(groupId));

}

export function isChangedKeyNewFromAddedGroup(
  currentPathSources: string[],
  previousPathSources: string[],
  addedGroupIds: Set<string>,
): boolean {

  if (previousPathSources.length > 0) {

    return false;

  }
  return currentPathSources.some((groupId) => addedGroupIds.has(groupId));

}

const uniqColorOptionsForClientConfigGroups = {
  saturation: [100, 100] as [number, number],
  lightness: [10, 80] as [number, number],
};

export function getClientConfigGroupColor(groupId: string): string {

  return uniqolor(groupId, uniqColorOptionsForClientConfigGroups).color;

}

export const CLIENT_CONFIG_INLINE_KEY_RAIL_COLOR = '#d9d9d9';

export function isConfigPathEqualOrDescendant(path: string, ancestorPath: string): boolean {

  return path === ancestorPath || path.startsWith(`${ancestorPath}.`);

}

export function hasActiveGroupSourcesAtOrUnderPath(
  sourcesByPath: Record<string, string[]>,
  path: string,
): boolean {

  return Object.keys(sourcesByPath).some(
    (sourcePath) => isConfigPathEqualOrDescendant(sourcePath, path)
      && (sourcesByPath[sourcePath]?.length ?? 0) > 0,
  );

}

function collectSourcePathsUnderPrefix(
  sourcesByPath: Record<string, string[]>,
  prefix: string,
): string[] {

  return Object.keys(sourcesByPath).filter((sourcePath) => isConfigPathEqualOrDescendant(sourcePath, prefix));

}

function moveGroupSourcesToOverriddenForPathAndDescendants(
  prefix: string,
  sourcesByPath: Record<string, string[]>,
  overriddenSourcesByPath: Record<string, string[]>,
): void {

  for (const path of collectSourcePathsUnderPrefix(sourcesByPath, prefix)) {

    const previousSources = sourcesByPath[path];
    if (!previousSources?.length) {

      continue;

    }
    overriddenSourcesByPath[path] = [...previousSources];
    delete sourcesByPath[path];

  }

}

export function getFirstAppConfigEntry(record: any): AppConfigEntry | null {

  const appConfig = record?.clientConfig?.app;
  if (!Array.isArray(appConfig) || appConfig.length === 0) {

    return null;

  }
  const first = appConfig[0];
  return {
    definitions: Array.isArray(first?.definitions) ? first.definitions : [],
    filter: first?.filter,
  };

}

export type ClientConfigAttachmentLevel = 'customer' | 'group' | 'device';

/** First attachment wins (call layers in cascade order: customer → asset group → device). */
export function buildGroupAttachmentLevelById(
  layers: Array<{ level: ClientConfigAttachmentLevel; definitions: AppDefinition[] }>,
  groupById: Record<string, ClientConfigGroupLike>,
): Record<string, ClientConfigAttachmentLevel> {

  const result: Record<string, ClientConfigAttachmentLevel> = {};
  const visited = new Set<string>();

  const walk = (definitions: AppDefinition[], level: ClientConfigAttachmentLevel) => {

    for (const definition of definitions || []) {

      if (definition?.type !== 'group') {

        continue;

      }
      const groupId = definition.group?._id;
      if (!groupId || visited.has(groupId)) {

        continue;

      }
      visited.add(groupId);
      result[groupId] = level;
      const nestedDefinitions = getFirstAppConfigEntry(groupById[groupId])?.definitions || [];
      walk(nestedDefinitions, level);

    }

  };

  for (const layer of layers) {

    walk(layer.definitions, layer.level);

  }
  return result;

}

export function splitDefinitions(definitions: AppDefinition[]) {

  const groupDefinitions: AppGroupDefinition[] = [];
  const keyDefinitions: AppKeyDefinition[] = [];
  for (const definition of definitions || []) {

    if (definition?.type === 'group') {

      groupDefinitions.push(definition);

    } else if (definition?.type === 'key') {

      keyDefinitions.push(definition);

    }

  }
  return { groupDefinitions, keyDefinitions };

}

const sortKeyDefinitions = (definitions: AppKeyDefinition[]) => (
  [...definitions].sort((left, right) => left.key.localeCompare(right.key))
);

export function areKeyDefinitionsEqual(
  left: AppKeyDefinition[],
  right: AppKeyDefinition[],
): boolean {

  return _.isEqual(sortKeyDefinitions(left), sortKeyDefinitions(right));

}

export function findKeyDefinitionByPath(
  definitions: AppKeyDefinition[],
  path: string,
): AppKeyDefinition | undefined {

  return definitions.find((definition) => definition.key === path);

}

export function removeKeyDefinitionByPath(
  definitions: AppKeyDefinition[],
  path: string,
): AppKeyDefinition[] {

  return definitions.filter((definition) => definition.key !== path);

}

function applyDefinitionsRecursive(
  config: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
  visitedGroups: Set<string>,
) {

  // Match main-api resolve: apply definitions in array order (later wins).
  for (const definition of definitions || []) {

    if (definition?.type === 'group') {

      const groupId = definition.group?._id;
      if (!groupId || visitedGroups.has(groupId)) {

        continue;

      }
      const group = groupById[groupId];
      if (!group) {

        continue;

      }
      visitedGroups.add(groupId);
      const nestedDefinitions = getFirstAppConfigEntry(group)?.definitions || [];
      applyDefinitionsRecursive(config, nestedDefinitions, groupById, visitedGroups);
      visitedGroups.delete(groupId);

    } else if (definition?.type === 'key') {

      _.set(config, definition.key, definition.value);

    }

  }

}

function applyDefinitionsRecursiveWithSources(
  config: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
  visitedGroups: Set<string>,
  sourcesByPath: Record<string, string[]>,
  overriddenSourcesByPath: Record<string, string[]>,
  groupStack: string[],
) {

  // Match main-api resolve: apply definitions in array order (later wins);
  // nested groups are expanded in place when encountered.
  for (const definition of definitions || []) {

    if (definition?.type === 'group') {

      const groupId = definition.group?._id;
      if (!groupId || visitedGroups.has(groupId)) {

        continue;

      }

      const group = groupById[groupId];
      if (!group) {

        continue;

      }

      visitedGroups.add(groupId);
      groupStack.push(groupId);
      const nestedDefinitions = getFirstAppConfigEntry(group)?.definitions || [];
      applyDefinitionsRecursiveWithSources(
        config,
        nestedDefinitions,
        groupById,
        visitedGroups,
        sourcesByPath,
        overriddenSourcesByPath,
        groupStack,
      );
      groupStack.pop();
      visitedGroups.delete(groupId);

    } else if (definition?.type === 'key') {

      if (groupStack.length === 0) {

        moveGroupSourcesToOverriddenForPathAndDescendants(
          definition.key,
          sourcesByPath,
          overriddenSourcesByPath,
        );

      }

      _.set(config, definition.key, definition.value);
      if (groupStack.length > 0) {

        const existing = sourcesByPath[definition.key] || [];
        const next = [...existing];
        for (const groupId of groupStack) {

          if (!next.includes(groupId)) {

            next.push(groupId);

          }

        }
        sourcesByPath[definition.key] = next;

      }

    }

  }

}

export function buildConfigFromDefinitions(
  defaultConfig: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
) {

  const config = _.cloneDeep(defaultConfig || {});
  applyDefinitionsRecursive(config, definitions, groupById, new Set<string>());
  return config;

}

/** Apply definition layers in cascade order (each layer in definition array order; later wins; nested groups expanded in place). */
export function buildConfigFromDefinitionLayersWithSources(
  defaultConfig: Record<string, any>,
  layers: AppDefinition[][],
  groupById: Record<string, ClientConfigGroupLike>,
) {

  const config = _.cloneDeep(defaultConfig || {});
  const sourcesByPath: Record<string, string[]> = {};
  const overriddenSourcesByPath: Record<string, string[]> = {};
  for (const definitions of layers) {

    if (!definitions?.length) {

      continue;

    }
    applyDefinitionsRecursiveWithSources(
      config,
      definitions,
      groupById,
      new Set<string>(),
      sourcesByPath,
      overriddenSourcesByPath,
      [],
    );

  }
  return {
    config,
    sourcesByPath,
    overriddenSourcesByPath,
  };

}

export function buildConfigFromDefinitionsWithSources(
  defaultConfig: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
) {

  return buildConfigFromDefinitionLayersWithSources(defaultConfig, [definitions], groupById);

}

export function getChangedObject(base: any, current: any): any {

  if (_.isEqual(base, current)) {

    return undefined;

  }
  if (!_.isPlainObject(current) || !_.isPlainObject(base)) {

    return current;

  }

  const changed: Record<string, any> = {};
  const keys = _.uniq([...Object.keys(base || {}), ...Object.keys(current || {})]);
  for (const key of keys) {

    const next = getChangedObject(base?.[key], current?.[key]);
    if (next !== undefined) {

      changed[key] = next;

    }

  }
  return changed;

}

export function flattenChangedLeafPaths(changed: any, prefix = ''): Array<{ path: string; value: any }> {

  if (!_.isPlainObject(changed) || Array.isArray(changed)) {

    return [{ path: prefix, value: changed }];

  }

  const entries: Array<{ path: string; value: any }> = [];
  for (const key of Object.keys(changed)) {

    const nextPath = prefix ? `${prefix}.${key}` : key;
    const value = changed[key];
    if (_.isPlainObject(value) && !Array.isArray(value)) {

      entries.push(...flattenChangedLeafPaths(value, nextPath));

    } else {

      entries.push({ path: nextPath, value });

    }

  }

  return entries;

}

export type ChangedPreviewNode = {
  key: string;
  path: string;
  value: any;
  isObjectNode: boolean;
  children: ChangedPreviewNode[];
  sourceGroupIds: string[];
};

function aggregateSourceIdsFromChildren(children: ChangedPreviewNode[]): string[] {

  const result: string[] = [];
  for (const child of children) {

    for (const sourceGroupId of child.sourceGroupIds) {

      if (!result.includes(sourceGroupId)) {

        result.push(sourceGroupId);

      }

    }

  }
  return result;

}

export function buildChangedPreviewTree(
  changed: any,
  sourcesByPath: Record<string, string[]>,
  prefix = '',
): ChangedPreviewNode[] {

  if (!_.isPlainObject(changed) || Array.isArray(changed)) {

    return [];

  }

  const nodes: ChangedPreviewNode[] = [];
  for (const key of Object.keys(changed)) {

    const path = prefix ? `${prefix}.${key}` : key;
    const value = changed[key];
    const isObjectNode = _.isPlainObject(value) && !Array.isArray(value);
    const children = isObjectNode ? buildChangedPreviewTree(value, sourcesByPath, path) : [];
    const sourceGroupIds = isObjectNode
      ? aggregateSourceIdsFromChildren(children)
      : (sourcesByPath[path] || []);

    nodes.push({
      key,
      path,
      value,
      isObjectNode,
      children,
      sourceGroupIds,
    });

  }

  const collapseSingleChildObjects = (node: ChangedPreviewNode): ChangedPreviewNode => {

    if (!node.isObjectNode) {

      return node;

    }

    const collapsedChildren = node.children.map((child) => collapseSingleChildObjects(child));
    let collapsedNode: ChangedPreviewNode = {
      ...node,
      children: collapsedChildren,
      sourceGroupIds: aggregateSourceIdsFromChildren(collapsedChildren),
    };

    while (collapsedNode.isObjectNode && collapsedNode.children.length === 1) {

      const onlyChild = collapsedNode.children[0];
      collapsedNode = {
        ...onlyChild,
        key: `${collapsedNode.key}.${onlyChild.key}`,
      };

    }

    return collapsedNode;

  };

  return nodes.map((node) => collapseSingleChildObjects(node));

}
